<template>
  <AnimatePresence mode="wait">
    <PageContainer
      :key="$route.path"
      class="page--container"
      :class="{
        'has-header': $slots['page-header'],
        'has-footer': $slots['page-footer']
      }"
      :initial="{ opacity: 0, visibility: 'hidden' }"
      :animate="{ opacity: 1, visibility: 'visible' }"
      :exit="{ opacity: 0, visibility: 'hidden' }"
      :transition="{ duration: 0.3 }"
    >
      <h1
        v-if="title"
        class="page--title"
      >
        {{ title }}
      </h1>

      <div
        v-if="$slots['page-header']"
        class="page--header"
      >
        <slot name="page-header" />
      </div>

      <div
        v-if="$slots['page-body']"
        class="page--body"
      >
        <q-scroll-area
          v-if="isScrollable"
          ref="scrollAreaRef"
          class="page--scrollarea"
        >
          <div class="page--scrollarea--inner">
            <slot name="page-body" />
          </div>
        </q-scroll-area>
        <div
          v-else
          class="page--inner"
        >
          <slot name="page-body" />
        </div>
      </div>

      <div
        v-if="$slots['page-footer']"
        class="page--footer"
      >
        <slot name="page-footer" />
      </div>
    </PageContainer>
  </AnimatePresence>
</template>

<script setup>
import { provide, useTemplateRef, ref, useSlots, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { AnimatePresence, motion } from 'motion-v'

defineOptions({ name: 'PageContainer' })
const props = defineProps({
  // Visually hidden <h1> for screen readers; pages in this layout have no visible title.
  title: { type: String, default: '' },
  isScrollable: { type: Boolean, default: true }
})

const PageContainer = motion.create('div')

const $slots = useSlots()
const $route = useRoute()

const scrollAreaRef = useTemplateRef('scrollAreaRef')
const scrollTarget = ref(null)

// Provides a reactive ref to the QScrollArea's underlying DOM scroll element.
// Ref is null until after mount — consumers (e.g. useVirtualRows) that
// read from it will react automatically when it becomes available.
provide('pageScrollTarget', scrollTarget)

onMounted(() => {
  scrollTarget.value = scrollAreaRef.value?.getScrollTarget() ?? null
})
</script>

<style scoped>
@reference "#app-styles.css";

.page--container { @apply flex flex-col h-full; }

.page--container::before {
  @apply content-[''] absolute -top-(--header-height) right-0 bottom-0 left-0 -z-1;
  @apply rounded-lg surface-surf shadow-lg;
}

.page--title { @apply sr-only; }

.page--header {
  @apply min-h-(--header-height);
  &:deep(> .q-toolbar) {
    @apply min-h-0;

    .left { @apply grow; }
    .right { @apply flex items-center gap-2; }
  }
}

.page--body { @apply grow rounded-b-xl; }
.page--container.has-footer .page--body { @apply rounded-b-none; }

.page--scrollarea { @apply h-full; }
.page--scrollarea--inner { @apply flex flex-col w-full; }
.page--inner { @apply flex flex-col w-full h-full; }

.page--footer { @apply rounded-b-xl; }
</style>
